import { CalendarClock, AlarmClock, CalendarCheck, AlertTriangle } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { stageBadge, isFollowupDue, isOverdueFollowup, hasNoNextAction, CLOSED_STAGES } from "@/data/investorOptions";

const startOfWeekEnd = () => {
  const d = new Date();
  const end = new Date(d);
  end.setDate(d.getDate() + 7);
  return end.toISOString().slice(0, 10);
};

const line = (onOpen) => (i) => (
  <li key={i.id}>
    <button onClick={() => onOpen(i)} className="flex w-full items-start justify-between gap-3 border-b border-[#E4E9F3]/60 pb-3 text-left last:border-0 last:pb-0">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-[#14245F]">{i.name}</p>
        <p className="text-xs text-[#5A6B8C]">{i.next_follow_up_date ? `Follow-up ${i.next_follow_up_date}` : i.meeting_date ? `Meeting ${i.meeting_date}` : i.contact_person || ""}</p>
      </div>
      <StatusBadge status={stageBadge(i.stage)} label={i.stage} />
    </button>
  </li>
);

const List = ({ items, empty, onOpen, testid }) =>
  items.length === 0
    ? <EmptyState icon={empty.icon} title={empty.title} description={empty.desc} />
    : <ul className="space-y-3" data-testid={testid}>{items.map(line(onOpen))}</ul>;

export const InvestorMeetings = ({ investors, today, onOpen }) => {
  const weekEnd = startOfWeekEnd();
  const dueToday = investors.filter((i) => isFollowupDue(i, today) && i.next_follow_up_date === today);
  const dueThisWeek = investors.filter((i) => !isOverdueFollowup(i, today) && i.next_follow_up_date && i.next_follow_up_date > today && i.next_follow_up_date <= weekEnd && !CLOSED_STAGES.includes(i.stage));
  const overdue = investors.filter((i) => isOverdueFollowup(i, today));
  const meetings = investors.filter((i) => i.meeting_date && i.meeting_date >= today);
  const noAction = investors.filter(hasNoNextAction);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="investor-meetings">
      <SectionCard testid="due-today-card" title="Follow-ups Due Today"><List testid="due-today-list" items={dueToday} onOpen={onOpen} empty={{ icon: CalendarClock, title: "None due today", desc: "Follow-ups scheduled for today appear here." }} /></SectionCard>
      <SectionCard testid="due-week-card" title="Follow-ups Due This Week"><List testid="due-week-list" items={dueThisWeek} onOpen={onOpen} empty={{ icon: CalendarClock, title: "Nothing this week", desc: "Follow-ups due within 7 days appear here." }} /></SectionCard>
      <SectionCard testid="overdue-card" title="Overdue Follow-ups"><List testid="overdue-list" items={overdue} onOpen={onOpen} empty={{ icon: AlarmClock, title: "Nothing overdue", desc: "Overdue follow-ups appear here." }} /></SectionCard>
      <SectionCard testid="meetings-card" title="Meetings Scheduled"><List testid="meetings-list" items={meetings} onOpen={onOpen} empty={{ icon: CalendarCheck, title: "No meetings", desc: "Upcoming meetings appear here." }} /></SectionCard>
      <SectionCard testid="no-action-card" className="lg:col-span-2" title="Investors Without a Next Action"><List testid="no-action-list" items={noAction} onOpen={onOpen} empty={{ icon: AlertTriangle, title: "All covered", desc: "Active investors missing a next action will appear here." }} /></SectionCard>
    </div>
  );
};
